// MUI
import { Grid, Stack } from "@mui/material";
import { SnackbarProvider } from "notistack";

// Hooks
import { useLocation } from "@Common/hooks/Location/useLocation";

// Components
import { AUTO_HIDE_DURATION, MAX_SNACK, SnackbarsCustomStyle } from "@Layouts/components/Snackbars";
import ApprovedMessage from "@/Layouts/components/ApprovedMessage";
import ReportButtonLinkProgress from "@/Layouts/components/Buttons/ReportButtonLinkProgress";
import EmployeeMeta from "@/Layouts/components/EmployeeMeta";

// Helpers
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Types
import { EmployeeMetaSectionProps } from "@/Washouts/types/commons";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

// Icons
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import DoneIcon from "@mui/icons-material/Done";

export default function EmployeeMetaSection(props: Readonly<EmployeeMetaSectionProps>) {
	const {
		model,
		loading = true,
		hidden = false,
		columns = [
			["POSITION", "DEPARTMENT", "REPORTS_TO"],
			["ID", "EMAIL", "PHONE"],
			["PAY_DATE", "PAY_RANGE", "STORE"],
		],
		secondaryColumns = [],
		hiddenReportButton = false,
	} = props;

	const { timezone } = useLocation() ?? {};

	const position = model?.employee?.position ?? "";
	const washoutId = model?._id ?? "";
	const fileName = model?.employee?.name + ".pdf";

	return (
		<Grid item>
			{!hidden && (
				<EmployeeMeta
					{...props}
					data={model?.employee}
					columns={columns}
					secondaryColumn={secondaryColumns}
					loading={loading}
					bottomDivider
					sectionDivider
					arrow
					isCollapsible
					addons={
						(Boolean(model?.review?.status) || !hiddenReportButton) && (
							<Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} spacing={2.6}>
								{Boolean(model?.review?.status) && (
									<Stack>
										<ApprovedMessage
											title={`Approved by ${model?.review?.statusSetBy?.fullName} on ${getFormattedDate(
												model?.review?.statusSetOn,
												timezone,
												DATE_FORMAT.monthAbbrDatePadded
											)}`}
											status={model?.review?.status ?? ""}
											visibility={Boolean(model?.review?.status)}
										/>
									</Stack>
								)}
								{!hiddenReportButton && (
									<Stack>
										<SnackbarProvider
											maxSnack={MAX_SNACK}
											anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
											autoHideDuration={AUTO_HIDE_DURATION}
											iconVariant={{
												error: <ErrorOutlineRoundedIcon fontSize="small" className="mr-2" />,
												success: <DoneIcon fontSize="small" className="mr-2" />,
											}}
											Components={{
												error: SnackbarsCustomStyle,
												success: SnackbarsCustomStyle,
											}}
										>
											<ReportButtonLinkProgress
												disabled={false}
												to={""}
												sx={{ display: "inline-block", backgroundColor: "transparent" }}
												sxButton={{ color: "var(--text-color)" }}
												position={position}
												washoutId={washoutId}
												fileName={fileName}
											/>
										</SnackbarProvider>
									</Stack>
								)}
							</Stack>
						)
					}
				/>
			)}
		</Grid>
	);
}
